import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  protocolAvailability,
  type ModelCatalogEntry,
  type PlatformBrainCuration,
  type PlatformPiCuration,
  type PlatformToolCuration as PlatformToolCurationData,
} from "@dosia/shared";
import { useApiQuery } from "../lib/useApiQuery.js";
import { usePlatformModelCatalog } from "../lib/usePlatformModelCatalog.js";
import {
  platformBrainCurationPath,
  platformPiCurationPath,
  platformToolCurationPath,
} from "../lib/api-paths.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { GatewayTierDefaultsPanel } from "../components/GatewayTierDefaultsPanel.js";
import { protocolLabel } from "./PlatformModelGovernance.js";

/**
 * 「模型清单」数据枢纽页（P3，2026-07）—— DOSIA 超管侧**唯一模型数据源展示面**。
 *
 * 网关下发的全量数据一屏看全：每个模型在每个分组下的协议可用性、刊例价、可调参数
 * （已实测/推导）、上下文档位。三个模型池（Claude SDK 主脑 / pi 框架主脑 / 工具模型）
 * 全部从 usePlatformModelCatalog() 同一份数据取数——本页只做**展示**，不做池准入编辑
 * （池准入仍在各自策展页：/admin/model-governance 与其 tools tab）。
 *
 * 池归属列只反映**已显式策展**的收录（brain-curation.allowedBrainModels /
 * pi-curation.models / tool-curation.modelCallModels 三份既有存量数据），策展字段
 * undefined（未配置=全放行/未策展）时不臆造"全员入池"的假象，如实显示"未入池"。
 */

type Row = {
  base: string;
  entries: ModelCatalogEntry[];
  displayEntry: ModelCatalogEntry;
};

const baseId = (modelId: string): string => modelId.replace(/\[[^\]]*\]/g, "");

function formatMoney(v: number | undefined): string | undefined {
  if (typeof v !== "number") return undefined;
  return `$${v}`;
}

export function PlatformModelCatalogHub(): JSX.Element {
  const { t } = useTranslation();
  const { entries, source, loading } = usePlatformModelCatalog();
  const brainQ = useApiQuery<{ curation: PlatformBrainCuration | null }>(platformBrainCurationPath(), []);
  const piQ = useApiQuery<{ curation: PlatformPiCuration | null }>(platformPiCurationPath(), []);
  const toolQ = useApiQuery<{ curation: PlatformToolCurationData | null }>(platformToolCurationPath(), []);

  const [search, setSearch] = useState("");
  const [family, setFamily] = useState<string>("all");
  const [openRow, setOpenRow] = useState<string | null>(null);

  const brainSet = useMemo(
    () => new Set((brainQ.data?.curation?.allowedBrainModels ?? []).map(baseId)),
    [brainQ.data],
  );
  const piSet = useMemo(
    () => new Set((piQ.data?.curation?.models ?? []).map(baseId)),
    [piQ.data],
  );
  const toolSet = useMemo(
    () => new Set((toolQ.data?.curation?.modelCallModels ?? []).map(baseId)),
    [toolQ.data],
  );

  const rows = useMemo<Row[]>(() => {
    const grouped = new Map<string, ModelCatalogEntry[]>();
    for (const entry of entries) {
      const key = baseId(entry.modelId);
      if (!key) continue;
      const list = grouped.get(key) ?? [];
      list.push(entry);
      grouped.set(key, list);
    }
    return [...grouped.entries()]
      .map(([base, es]) => ({ base, entries: es, displayEntry: es[0]! }))
      .sort((a, b) => a.base.localeCompare(b.base));
  }, [entries]);

  const families = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const f = r.displayEntry.family?.trim();
      if (f) set.add(f);
    }
    return [...set].sort();
  }, [rows]);

  /** 行的协议可用性档位聚合：每条 catalog 行按 protocolAvailability 分 up(可用/未知)/down(降级/不支持)。 */
  const rowAvailability = useMemo(() => {
    const map = new Map<string, "full" | "partial" | "dead">();
    for (const r of rows) {
      let up = 0;
      let down = 0;
      for (const e of r.entries) {
        const level = protocolAvailability(e).level;
        if (level === "degraded" || level === "unsupported") down++;
        else up++;
      }
      map.set(r.base, down === 0 ? "full" : up === 0 ? "dead" : "partial");
    }
    return map;
  }, [rows]);

  const stats = useMemo(() => {
    let full = 0, partial = 0, dead = 0;
    for (const r of rows) {
      const level = rowAvailability.get(r.base);
      if (level === "full") full++;
      else if (level === "dead") dead++;
      else partial++;
    }
    return { total: rows.length, full, partial, dead };
  }, [rows, rowAvailability]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (family !== "all" && r.displayEntry.family !== family) return false;
      if (!q) return true;
      return r.base.toLowerCase().includes(q) || (r.displayEntry.displayName ?? "").toLowerCase().includes(q);
    });
  }, [rows, family, search]);

  return (
    <div className="max-w-6xl space-y-4">
      <header>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold">模型清单</h1>
          {loading && <span className="text-xs text-dosia-text-secondary">加载中…</span>}
        </div>
        <p className="mt-1 text-sm text-dosia-text-secondary">
          网关下发的全量数据源一屏看全：每个模型在每个分组下的协议可用性、刊例价、可调参数
          （已实测 / 推导）、上下文档位。Claude SDK 主脑池、pi 框架主脑池、工具模型池全部从这里取数，
          池页面不再重复这些细节——池准入/默认参数仍在<span className="font-medium">平台模型治理</span>页编辑。
        </p>
        {source !== "live" && !loading && (
          // 网关不可达：清单如实留空（不回落随包快照，N-95），hook 每 30s 后台重拉，恢复后自动出现。
          <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs font-medium text-amber-400">
            {t("admin.modelCatalog.unavailable")}
          </div>
        )}
      </header>

      {(brainQ.error || piQ.error || toolQ.error) && (
        <ErrorBanner>{brainQ.error?.message ?? piQ.error?.message ?? toolQ.error?.message}</ErrorBanner>
      )}

      <GatewayTierDefaultsPanel />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["全量模型", stats.total, ""],
          ["全协议可用", stats.full, "text-emerald-500"],
          ["部分协议降级", stats.partial, "text-amber-400"],
          ["无存活上游", stats.dead, "text-red-400"],
        ].map(([label, value, cls]) => (
          <div key={label as string} className="rounded border border-dosia-border bg-dosia-bg-light px-3 py-2">
            <div className="text-xs text-dosia-text-secondary">{label}</div>
            <div className={`mt-1 text-xl font-semibold ${cls || "text-dosia-text"}`}>{value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索模型 ID / 展示名…"
          aria-label="搜索模型"
          className="min-w-[200px] flex-1 rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text placeholder:text-dosia-text-dim focus:outline-none focus:border-dosia-accent"
        />
        <button
          type="button"
          onClick={() => setFamily("all")}
          className={`rounded-full px-3 py-1 text-xs ${family === "all" ? "bg-dosia-accent text-white" : "border border-dosia-border text-dosia-text-secondary"}`}
        >
          全部
        </button>
        {families.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFamily(f)}
            className={`rounded-full px-3 py-1 text-xs ${family === f ? "bg-dosia-accent text-white" : "border border-dosia-border text-dosia-text-secondary"}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-dosia-border">
        <table className="w-full text-sm">
          <thead className="bg-dosia-bg-light text-dosia-text-secondary">
            <tr>
              <th className="px-3 py-2 text-left font-medium">模型</th>
              <th className="px-3 py-2 text-left font-medium">承载分组</th>
              <th className="px-3 py-2 text-left font-medium">协议可用性</th>
              <th className="px-3 py-2 text-left font-medium">刊例价 /M</th>
              <th className="px-3 py-2 text-left font-medium">参数 · 能力</th>
              <th className="px-3 py-2 text-left font-medium">池</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const { base, entries: modelEntries, displayEntry } = row;
              const isOpen = openRow === base;
              const groupNames = [...new Set(
                modelEntries.flatMap((e) => [e.chosenGroup?.name, ...(e.availableGroups ?? []).map((g) => g.name)].filter((n): n is string => Boolean(n))),
              )];
              const pricingEntry = modelEntries.find((e) => e.pricing) ?? displayEntry;
              const p = pricingEntry.pricing;
              const contextWindow = displayEntry.capabilities?.contextWindow;
              const supports1m = modelEntries.some((e) => e.capabilities?.supports1MContext);
              const capChips = [...new Set(modelEntries.flatMap((e) => e.capabilityTags ?? []))].slice(0, 3);
              const verified = modelEntries.some((e) => e.capabilities?.paramsVerified === true);
              const pools = [
                brainSet.has(base) ? "SDK主脑" : null,
                piSet.has(base) ? "pi主脑" : null,
                toolSet.has(base) ? "工具" : null,
              ].filter((x): x is string => Boolean(x));
              return (
                <Fragment key={base}>
                  <tr
                    className="cursor-pointer border-t border-dosia-border align-top hover:bg-dosia-bg-lighter"
                    onClick={() => setOpenRow((cur) => (cur === base ? null : base))}
                  >
                    <td className="px-3 py-3">
                      <div className="font-mono font-medium text-dosia-text">{base}</div>
                      <div className="text-xs text-dosia-text-secondary">{displayEntry.displayName || ""}</div>
                    </td>
                    <td className="px-3 py-3 text-dosia-text-secondary">
                      <div className="flex flex-wrap gap-1">
                        {(groupNames.length > 0 ? groupNames : ["—"]).map((name) => (
                          <span key={name} className="rounded bg-dosia-bg-lighter px-1.5 py-0.5 text-xs">{name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {modelEntries.map((e) => {
                          const avail = protocolAvailability(e);
                          const icon = avail.level === "degraded" ? "◐" : avail.level === "unsupported" ? "—" : "●";
                          const cls = avail.level === "degraded" ? "text-amber-400" : avail.level === "unsupported" ? "text-dosia-text-dim" : "text-emerald-500";
                          return (
                            <span key={e.protocol} className={`text-xs ${cls}`} title={avail.label || protocolLabel(e.protocol)}>
                              {icon} {protocolLabel(e.protocol)}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-dosia-text-secondary">
                      {p ? (
                        <>入 {formatMoney(p.inputPer1m) ?? "—"} · 出 {formatMoney(p.outputPer1m) ?? "—"}</>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-1 text-xs">
                        {capChips.map((c) => <span key={c} className="rounded bg-dosia-bg-lighter px-1.5 py-0.5">{c}</span>)}
                        {contextWindow && <span className="rounded bg-dosia-bg-lighter px-1.5 py-0.5">{Math.round(contextWindow / 1000)}k{supports1m ? " · 1M" : ""}</span>}
                        <span className={`rounded px-1.5 py-0.5 font-medium ${verified ? "bg-dosia-accent/15 text-dosia-accent" : "bg-dosia-bg-lighter text-dosia-text-secondary"}`}>
                          {verified ? "已实测" : "推导"}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {pools.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {pools.map((p2) => <span key={p2} className="rounded bg-dosia-accent/15 px-1.5 py-0.5 text-xs font-medium text-dosia-accent">{p2}</span>)}
                        </div>
                      ) : <span className="text-xs text-dosia-text-secondary">未入池</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-dosia-border bg-dosia-bg-light">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid gap-3 md:grid-cols-3">
                          <div className="rounded border border-dosia-border bg-dosia-bg px-3 py-2 text-xs text-dosia-text-secondary">
                            <h5 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-dosia-text-dim">刊例价</h5>
                            {p ? (
                              <div className="space-y-0.5 font-mono">
                                <div>输入 {formatMoney(p.inputPer1m) ?? "—"} /M</div>
                                <div>输出 {formatMoney(p.outputPer1m) ?? "—"} /M</div>
                                {typeof p.cacheReadPer1m === "number" && <div>缓存读 {formatMoney(p.cacheReadPer1m)} /M</div>}
                                {typeof p.cacheWritePer1m === "number" && <div>缓存写 {formatMoney(p.cacheWritePer1m)} /M</div>}
                              </div>
                            ) : "网关未下发刊例价"}
                          </div>
                          <div className="rounded border border-dosia-border bg-dosia-bg px-3 py-2 text-xs text-dosia-text-secondary">
                            <h5 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-dosia-text-dim">上下文</h5>
                            <div>{contextWindow ? `${contextWindow.toLocaleString()} tokens` : "网关未声明"}{supports1m ? " · 1M 变体可用" : ""}</div>
                          </div>
                          <div className="rounded border border-dosia-border bg-dosia-bg px-3 py-2 text-xs text-dosia-text-secondary">
                            <h5 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-dosia-text-dim">逐协议参数支持</h5>
                            <div className="space-y-1.5">
                              {modelEntries.map((e) => {
                                const avail = protocolAvailability(e);
                                const caps = e.capabilities;
                                const chips: string[] = [];
                                if (caps?.supportsVision) chips.push("vision");
                                if (caps?.supportsToolUse) chips.push("tools");
                                if (caps?.supportsThinking || caps?.thinkingParam?.supported) chips.push("thinking");
                                if (caps?.supportsEffort) chips.push("effort");
                                return (
                                  <div key={e.protocol}>
                                    <b className="text-dosia-text">{protocolLabel(e.protocol)}</b>
                                    <span className="ml-1 text-dosia-text-dim">
                                      {avail.label ? `（${avail.label}）` : ""}
                                    </span>
                                    <div>{chips.length > 0 ? chips.join(" · ") : "未声明能力标签"}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-dosia-text-secondary">
                  没有匹配的模型
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
